"use client"

import type { InputHTMLAttributes, Ref } from "react"

import { ArrowIcon, HeaderSearchIcon, SearchIcon } from "@/components/ui/icons"
import { Text } from "@/components/ui/text"
import { tv, type VariantProps } from "@/lib/utils/tv"

const searchInput = tv({
  base: "field-focus-ring flex items-center rounded-full",
  variants: {
    // `glass` is the D1 nav hero field (translucent pill inside the glass nav card).
    // `line` is the field for a white chrome: the ground is already white, so the
    // pill reads as an outline rather than a fill.
    tone: {
      newgray: "bg-surface-newgray",
      surface: "bg-surface",
      line: "bg-surface border-line focus-within:border-line-strong border transition-colors",
      chrome: "bg-surface ring-line-field focus-within:ring-line-strong ring-1 transition-shadow ring-inset",
      glass: "nav-search",
    },
    // `lg` is the D1 hero size: 48px tall with a wider left inset; `md` is the desktop
    // chrome field; `sm` is the default compact field used on listing/filter surfaces.
    size: { sm: "pointer-coarse:h-11 h-10 gap-200 pr-150 pl-400", md: "h-11 gap-250 pr-100 pl-500", lg: "h-12 gap-300 pr-150 pl-500", nav: "h-nav gap-300 pr-500 pl-500" },
  },
  defaultVariants: { tone: "newgray", size: "sm" },
})

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">, VariantProps<typeof searchInput> {
  onSubmitSearch?: () => void
  hint?: string
  submit?: boolean
  submitVariant?: "disc" | "glyph"
  glyph?: boolean
  ref?: Ref<HTMLInputElement>
}

export const SearchInput = ({
  tone,
  size,
  hint,
  className,
  onSubmitSearch,
  submit = true,
  submitVariant = "disc",
  glyph = true,
  ref,
  placeholder = "Busca productos, marcas y categorías",
  ...rest
}: SearchInputProps) => {
  // The large hero field submits with a prominent search-icon button (D1); the compact
  // field keeps the arrow affordance.
  const compact = size === undefined || size === "sm"
  const chrome = tone === "chrome"
  return (
    <div className={searchInput({ tone, size, className })}>
      {glyph && (
        <span className="text-fg-subtle">
          <SearchIcon size={compact ? 16 : 18} />
        </span>
      )}
      <input
        ref={ref}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        inputMode="search"
        enterKeyHint="search"
        className={`text-field text-fg w-full min-w-0 self-stretch border-0 bg-transparent outline-none ${chrome ? "placeholder:text-fg-heading/50 placeholder:tracking-snug" : "placeholder:text-fg-muted"} ${compact ? "placeholder:text-label" : ""}`}
        {...rest}
      />
      {hint && (
        <Text as="span" variant="caption" family="display" tone="muted" className="border-line-subtle rounded-4x border px-200 py-50 font-semibold whitespace-nowrap">
          {hint}
        </Text>
      )}
      {submit && (
        <button
          type={onSubmitSearch ? "button" : "submit"}
          aria-label="Buscar"
          onClick={onSubmitSearch}
          className={
            submitVariant === "glyph"
              ? "hit-target text-fg-brand hover:text-brand-hover focus-visible:ring-brand flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              : `hit-target bg-brand text-fg-inverted hover:bg-brand-hover focus-visible:ring-brand flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${compact ? "size-7" : size === "md" ? "size-9" : "size-9.5"}`
          }
        >
          {submitVariant === "glyph" ? <SearchIcon size={21} /> : compact ? <ArrowIcon size={14} /> : chrome ? <HeaderSearchIcon /> : <SearchIcon size={16} />}
        </button>
      )}
    </div>
  )
}
